import React, { useCallback, useEffect, useState } from 'react';
import Input from '../../components/ui/Input';
import { getApiErrorMessage } from '../../utils/errors';
import {
  createInstagramAccount,
  listInstagramAccounts,
  updateInstagramAccount,
} from '../../api/instagramAccounts';
import { buildAccountPayload } from './formUtils';

const emptyForm = {
  id: '',
  slug: '',
  label: '',
  ig_user_id: '',
  token: '',
  sort_order: '0',
  is_active: false,
};

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function ConnectionBadge({ status }) {
  if (status !== 'reconnect_required') {
    return null;
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
      Reconectar necessário
    </span>
  );
}

function displayTitle(entry) {
  return entry.username || entry.name || entry.label || entry.slug;
}

function InstagramCatalogAdmin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [tokenEditing, setTokenEditing] = useState(true);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listInstagramAccounts();
      setEntries(response.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o catálogo Instagram.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedMeta(null);
    setTokenEditing(true);
    setSuccess('');
    setError('');
  };

  const handleSelect = (entry) => {
    setForm({
      id: entry.id,
      slug: entry.slug,
      label: entry.label || '',
      ig_user_id: entry.ig_user_id || '',
      token: '',
      sort_order: String(entry.sort_order ?? 0),
      is_active: Boolean(entry.is_active),
    });
    setSelectedMeta({
      username: entry.username,
      name: entry.name,
      connection_status: entry.connection_status,
      connection_error: entry.connection_error,
      token_configured: entry.token_configured,
    });
    setTokenEditing(!entry.token_configured);
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = buildAccountPayload(form, { tokenEditing });

    try {
      if (form.id) {
        const updated = await updateInstagramAccount(form.id, payload);
        setSuccess(
          payload.token ? 'Conta e token atualizados.' : 'Conta atualizada. Token mantido.'
        );
        setSelectedMeta({
          username: updated.username,
          name: updated.name,
          connection_status: updated.connection_status,
          connection_error: updated.connection_error,
          token_configured: updated.token_configured,
        });
        setForm((prev) => ({
          ...prev,
          token: '',
          ig_user_id: updated.ig_user_id || prev.ig_user_id,
        }));
        setTokenEditing(!updated.token_configured);
      } else {
        await createInstagramAccount({
          slug: form.slug.trim().toLowerCase(),
          ...payload,
        });
        setSuccess('Conta criada.');
        resetForm();
      }
      await loadEntries();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a conta.'));
    } finally {
      setSaving(false);
    }
  };

  const tokenConfigured = Boolean(form.id && selectedMeta?.token_configured);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Catálogo Instagram</h1>
        <p className="text-sm text-content-muted mt-1">
          Gerencie contas Instagram disponíveis para os agentes.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-600">{success}</p>}

      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <section className="surface-card p-6 shadow-card-md space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-content-primary">Contas</h2>
            <button type="button" className="btn-secondary text-sm" onClick={resetForm}>
              Novo
            </button>
          </div>
          {loading ? (
            <p className="text-sm text-content-muted">Carregando...</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-content-muted">Nenhuma conta cadastrada.</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {entries.map((entry) => {
                const isSelected = form.id === entry.id;
                return (
                  <li key={entry.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(entry)}
                      className={`w-full py-3 text-left transition-colors ${
                        isSelected ? 'text-forge-600' : 'hover:text-forge-600'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-content-primary">{displayTitle(entry)}</p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <ConnectionBadge status={entry.connection_status} />
                          <StatusBadge active={entry.is_active} />
                        </div>
                      </div>
                      <p className="text-xs text-content-muted">
                        {entry.slug} · ID {entry.ig_user_id} ·{' '}
                        {entry.token_configured ? 'token configurado' : 'sem token'}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="surface-card p-6 shadow-card-md">
          <h2 className="text-lg font-semibold text-content-primary mb-4">
            {form.id ? `Editar: ${form.slug}` : 'Nova conta'}
          </h2>

          {form.id && selectedMeta && (
            <div className="mb-4 space-y-2 text-sm border-b border-slate-200 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <ConnectionBadge status={selectedMeta.connection_status} />
                {selectedMeta.connection_status === 'missing_token' && (
                  <span className="text-xs text-content-muted">Token não configurado</span>
                )}
                {selectedMeta.connection_status === 'error' && (
                  <span className="text-xs text-content-muted">
                    {selectedMeta.connection_error || 'Erro ao consultar a Graph API'}
                  </span>
                )}
                {selectedMeta.connection_status === 'reconnect_required' &&
                  selectedMeta.connection_error && (
                    <span className="text-xs text-content-muted">
                      {selectedMeta.connection_error}
                    </span>
                  )}
                {selectedMeta.connection_status === 'ok' && (
                  <span className="text-xs text-content-muted">
                    @{selectedMeta.username || '—'}
                    {selectedMeta.name ? ` · ${selectedMeta.name}` : ''}
                  </span>
                )}
              </div>
              <p className="text-xs text-content-muted">
                Token configurado: {selectedMeta.token_configured ? 'Sim' : 'Não'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              id="slug"
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              disabled={Boolean(form.id)}
              required
              hint="Identificador único interno (ex.: empresa-x)"
            />
            <Input
              id="label"
              label="Label"
              value={form.label}
              onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              required
            />
            <Input
              id="ig_user_id"
              label="Instagram user ID"
              value={form.ig_user_id}
              onChange={(e) => setForm((prev) => ({ ...prev, ig_user_id: e.target.value }))}
              hint="Opcional se o token for válido — descobrimos automaticamente via Graph /me"
            />
            {tokenConfigured && !tokenEditing ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-sm font-medium text-content-primary">
                  Access token: configurado
                </p>
                <p className="text-xs text-content-muted mt-1">
                  Atualizar a conta não altera o token guardado. Só clique em trocar se
                  for realmente substituir a credencial.
                </p>
                <button
                  type="button"
                  className="btn-secondary text-sm mt-3"
                  onClick={() => setTokenEditing(true)}
                >
                  Trocar token
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  id="token"
                  name="instagram-catalog-token"
                  label="Access token Meta"
                  type="password"
                  value={form.token}
                  onChange={(e) => setForm((prev) => ({ ...prev, token: e.target.value }))}
                  required={!form.id}
                  autoComplete="new-password"
                  hint={
                    tokenConfigured
                      ? 'Deixe vazio para manter o token atual.'
                      : 'Esta conta ainda não tem token configurado.'
                  }
                />
                {tokenConfigured && (
                  <button
                    type="button"
                    className="text-sm text-forge-600 hover:text-forge-700"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, token: '' }));
                      setTokenEditing(false);
                    }}
                  >
                    Cancelar troca de token
                  </button>
                )}
              </div>
            )}
            <Input
              id="sort_order"
              label="Ordem"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
            />
            <label className="flex items-center gap-2 text-sm text-content-muted">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
              Ativo
            </label>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : form.id ? 'Atualizar' : 'Criar'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

export default InstagramCatalogAdmin;
